/**
 * PATH: packages/job-hub/src/jobs/tabs/job-qa/JobQaInspectionSection.tsx
 * FILE_ID: JOBHUB-QA-INSPECTION-SECTION-001
 * PURPOSE: Render inspection loading, empty, selection, rename, and delete states.
 */
import { useRef, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { Loader2 } from 'lucide-react'
import { ContextMenu, MobileSheet, useContextMenu, useIsMdUp } from '@quotech/ui'
import type { QuoteRow } from '../../jobDetailTypes'
import { qaHubRecordUrl } from '../../qa/qaCrossDoorLinks'
import type { QaInspectionSummary } from '../../qa/qaApi'
import { InspectionChip } from '../JobQaListChips'
import { JobQaQuoteDetails } from './JobQaQuoteDetails'
import { QaRecordHome } from './QaRecordHome'
import { QaInspectionNavigator, type QaMenuOpenEvent } from './QaInspectionNavigator'
import { readQaClipboard, writeQaClipboard } from '../../qa/qaClipboard'
import { buildQaInspectionMenu, inspectionGroupChoices } from './qaInspectionMenu'
import { promptQaDuplicateCount } from './promptQaDuplicateCount'
import { useQaPaste } from './useQaPaste'
import { QA_TEST_TAB_ROW_CLASS } from './qaWorkspaceLayout'
import { addBlankArea } from './addBlankArea'
import type { QaWorkspaceController } from './useQaWorkspace'

export function JobQaInspectionSection({
  workspace,
  compact = false,
  chipsOnly = false,
  showRecordHome = false,
  onOpened,
  onPicked,
  jobId,
  orgId,
  quotes = [],
}: {
  workspace: QaWorkspaceController
  /** Desktop row under the setup cards: slightly smaller chips. */
  compact?: boolean
  /** Phone checklist header keeps the existing chip scroller. */
  chipsOnly?: boolean
  /** Desktop row and phone setup page. The checklist chips do not get this card. */
  showRecordHome?: boolean
  onOpened?: () => void
  onPicked?: (id: string) => void
  jobId?: number
  orgId?: number
  quotes?: readonly QuoteRow[]
}) {
  const isMdUp = useIsMdUp()
  const [settingsId, setSettingsId] = useState<string | null>(null)
  const [blankPending, setBlankPending] = useState(false)
  const [renameId, setRenameId] = useState<string | null>(null)
  const [renameRowId, setRenameRowId] = useState<string | null>(null)
  const [menuFrom, setMenuFrom] = useState<'row' | 'chip'>('chip')
  const [menuInspection, setMenuInspection] = useState<QaInspectionSummary | null>(null)
  const [moveIds, setMoveIds] = useState<string[]>([])
  const [jobRequest, setJobRequest] = useState<QaInspectionSummary | null>(null)
  const [groupRequestIds, setGroupRequestIds] = useState<string[] | null>(null)
  // Reused from: packages/job-hub/src/jobs/qa/QaChecklistTable.tsx (ContextMenu, useContextMenu)
  const menu = useContextMenu()
  const queryClient = useQueryClient()
  const pasted = useQaPaste({
    api: workspace.api,
    queryClient,
    jobId: jobId ?? 0,
    setError: workspace.setError,
  })
  // A second Duplicate while the first is still running would make a second set of copies.
  const duplicating = useRef(false)
  const duplicateCounted = async (ids: string[], count: number) => {
    if (duplicating.current) return
    duplicating.current = true
    try {
      for (const id of ids) {
        const result = await pasted.duplicateMany.mutateAsync({ id, count })
        const created = [...result.inspections].reverse().find((row) => row.id !== id)
        if (created) workspace.selectInspection(created.id)
      }
    } catch {
      // useQaPaste onError already shows the message.
    } finally {
      duplicating.current = false
    }
  }
  const settingsInspection = workspace.inspections.find((row) => row.id === settingsId)
  const active = workspace.inspections.find((row) => row.id === workspace.activeInspectionId) ?? null
  const addBlank = async () => {
    if (blankPending || !active || jobId == null) return
    setBlankPending(true)
    try {
      await addBlankArea(workspace, jobId)
    } finally {
      setBlankPending(false)
    }
  }
  if (workspace.inspectionsQuery.isLoading) {
    if (compact) {
      return (
        <div className="flex h-11 items-center text-muted-foreground">
          <Loader2 className="mr-2 h-5 w-5 animate-spin" aria-hidden /> Loading QA…
        </div>
      )
    }
    return (
      <div className="flex items-center justify-center py-12 text-muted-foreground">
        <Loader2 className="mr-2 h-5 w-5 animate-spin" aria-hidden /> Loading QA…
      </div>
    )
  }
  if (workspace.inspections.length === 0) {
    if (compact) return null
    return (
      <div className="rounded-xl border border-dashed border-border p-8 text-center text-sm text-muted-foreground">
        No QA lists yet. Tick one or more quotes above and choose Combine or Separate to set up QA.
      </div>
    )
  }

  const openMenu = (
    inspection: QaInspectionSummary,
    event: QaMenuOpenEvent,
    ids: string[],
    from: 'row' | 'chip',
  ) => {
    setMenuFrom(from)
    setMenuInspection(inspection)
    setMoveIds(ids)
    menu.openAt(event)
  }
  const duplicateHere = (inspection: QaInspectionSummary, count = 1) => {
    void duplicateCounted([inspection.id], count)
  }
  const askDuplicate = (inspection: QaInspectionSummary) => {
    void promptQaDuplicateCount(1).then((count) => {
      if (count != null) void duplicateCounted([inspection.id], count)
    })
  }
  const copiedQa = readQaClipboard()
  const pasteQaLabel = copiedQa?.kind === 'qa' ? copiedQa.label : null
  const assignGroup = (body: {
    inspection_ids: string[]
    job_id: string
    group_id?: string | null
    group_name?: string
  }) => {
    workspace.assignInspectionGroup.mutate(body)
  }

  return (
    <>
      {chipsOnly ? (
        <div className={QA_TEST_TAB_ROW_CLASS}>
          {workspace.inspections.map((inspection) => (
            <InspectionChip
              key={inspection.id}
              inspection={inspection}
              active={inspection.id === workspace.activeInspectionId}
              compact={compact}
              confirming={workspace.confirmDeleteId === inspection.id}
              deleting={workspace.remove.isPending && workspace.confirmDeleteId === inspection.id}
              renaming={
                workspace.renameInspection.isPending && workspace.renameInspection.variables?.id === inspection.id
              }
              renameRequested={renameId === inspection.id}
              onRenameStarted={() => setRenameId(null)}
              onClick={() => {
                workspace.selectInspection(inspection.id)
                onOpened?.()
              }}
              onRename={(name) => workspace.renameInspection.mutate({ id: inspection.id, name })}
              onRequestDelete={() => workspace.setConfirmDeleteId(inspection.id)}
              onCancelDelete={() => workspace.setConfirmDeleteId(null)}
              onConfirmDelete={() => workspace.remove.mutate(inspection.id)}
              onOpenSettings={() => setSettingsId(inspection.id)}
              onOpenMenu={(row, x, y) => openMenu(row, { clientX: x, clientY: y }, [row.id], 'chip')}
            />
          ))}
        </div>
      ) : (
        <div className="flex min-w-0 flex-wrap items-start gap-2">
          <QaInspectionNavigator
            inspections={workspace.inspections}
            activeInspectionId={workspace.activeInspectionId}
            jobId={jobId ?? 0}
            layout={compact ? 'row' : 'sheet'}
            onSelect={(id) => {
              workspace.selectInspection(id)
              onPicked?.(id)
              onOpened?.()
            }}
            onDuplicate={duplicateHere}
            onDuplicateToJob={(inspection, jobNumber) =>
              workspace.duplicateInspection.mutateAsync({ id: inspection.id, body: { job_id: jobNumber } })
            }
            onRenameSubmit={(inspection, name) => {
              workspace.selectInspection(inspection.id)
              workspace.renameInspection.mutate({ id: inspection.id, name })
            }}
            renamingId={
              workspace.renameInspection.isPending ? workspace.renameInspection.variables?.id ?? null : null
            }
            renameRowId={renameRowId}
            onRenameRowStarted={() => setRenameRowId(null)}
            onDelete={(inspection) => {
              workspace.selectInspection(inspection.id)
              workspace.remove.mutate(inspection.id)
            }}
            onAssignGroup={assignGroup}
            onDeleteSelected={async (ids) => {
              // Reused from: packages/job-hub/src/jobs/tabs/job-qa/useQaWorkspace.ts (remove, duplicateInspection)
              for (const id of ids) await workspace.remove.mutateAsync(id)
            }}
            onDuplicateSelected={duplicateCounted}
            onOpenMenu={(row, event, ids) => openMenu(row, event, ids, 'row')}
            jobRequest={jobRequest}
            onJobRequestClear={() => setJobRequest(null)}
            groupRequestIds={groupRequestIds}
            onGroupRequestClear={() => setGroupRequestIds(null)}
            onOpenSettings={() => { if (active) setSettingsId(active.id) }}
            settingsDisabled={active == null}
            onAddBlank={() => { void addBlank() }}
            blankPending={blankPending}
          />
          {showRecordHome ? (
            <QaRecordHome
              jobId={jobId ?? 0}
              orgId={orgId}
              inspectionId={workspace.activeInspectionId}
              quotes={quotes}
              sourceQuoteSheetIds={active?.source_quote_sheet_ids ?? []}
              listQDriveFiles={workspace.api.listQDriveFiles}
              layout={compact ? 'row' : 'sheet'}
            />
          ) : null}
        </div>
      )}
      <ContextMenu
        {...menu.props}
        items={menuInspection ? buildQaInspectionMenu({
          groups: inspectionGroupChoices(workspace.inspections),
          onOpenHub: () => {
            window.open(qaHubRecordUrl(menuInspection.id), '_blank', 'noopener,noreferrer')
          },
          onRename: () => {
            workspace.selectInspection(menuInspection.id)
            if (menuFrom === 'chip') setRenameId(menuInspection.id)
            else setRenameRowId(menuInspection.id)
          },
          onDuplicate: () => askDuplicate(menuInspection),
          onDuplicateToJob: () => {
            setGroupRequestIds(null)
            setJobRequest(menuInspection)
          },
          onMove: (groupId) => {
            const body: {
              inspection_ids: string[]
              job_id: string
              group_id?: string
            } = { inspection_ids: moveIds, job_id: String(jobId ?? '') }
            if (groupId) body.group_id = groupId
            assignGroup(body)
          },
          onNewGroup: () => {
            setJobRequest(null)
            setGroupRequestIds(moveIds)
          },
          onCopy: () => writeQaClipboard({
            kind: 'qa',
            ids: [menuInspection.id],
            label: menuInspection.inspection_name,
            jobId: jobId ?? 0,
            inspectionId: menuInspection.id,
          }),
          onPaste: () => {
            const clip = readQaClipboard()
            if (!clip || clip.kind !== 'qa' || !clip.ids[0]) return
            void pasted.pasteQa.mutateAsync(clip.ids[0]).then((created) => {
              workspace.selectInspection(created.id)
              setRenameId(created.id)
            })
          },
          pasteLabel: pasteQaLabel,
          onDelete: () => {
            workspace.selectInspection(menuInspection.id)
            if (menuFrom === 'chip') workspace.setConfirmDeleteId(menuInspection.id)
            else workspace.remove.mutate(menuInspection.id)
          },
        }) : []}
      />
      {settingsInspection && jobId != null && isMdUp ? (
        <JobQaQuoteDetails
          jobId={jobId}
          orgId={orgId}
          inspectionId={settingsInspection.id}
          quotes={quotes}
          sourceQuoteSheetIds={settingsInspection.source_quote_sheet_ids ?? []}
          onClose={() => setSettingsId(null)}
        />
      ) : null}
      {settingsInspection && jobId != null && !isMdUp ? (
        <MobileSheet open size="full" title="Settings (records)" onClose={() => setSettingsId(null)}>
          <JobQaQuoteDetails
            plain
            jobId={jobId}
            orgId={orgId}
            inspectionId={settingsInspection.id}
            quotes={quotes}
            sourceQuoteSheetIds={settingsInspection.source_quote_sheet_ids ?? []}
            onClose={() => setSettingsId(null)}
          />
        </MobileSheet>
      ) : null}
    </>
  )
}
